<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Article } from '../../types'
import { useNavigationStore } from '../../stores/navigationStore'
import PartCard from './PartCard.vue'
import PartRow from './PartRow.vue'
import SkeletonLoader from '../shared/SkeletonLoader.vue'
import ErrorDisplay from '../shared/ErrorDisplay.vue'
import ContactForm from '../shared/ContactForm.vue'

const props = defineProps<{
  articles: Article[]
  isLoading: boolean
  error: string | null
}>()

const emit = defineEmits<{
  retry: []
}>()

const navStore = useNavigationStore()

// View mode toggle — persisted in localStorage so the customer's preference
// sticks across navigation and reloads.
type ViewMode = 'grid' | 'list'
const VIEW_MODE_KEY = 'tecdoc.partsViewMode'

function loadViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_KEY)
    return stored === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

const viewMode = ref<ViewMode>(loadViewMode())

watch(viewMode, (next) => {
  try { localStorage.setItem(VIEW_MODE_KEY, next) } catch { /* ignore quota / privacy */ }
})

function setViewMode(mode: ViewMode) {
  viewMode.value = mode
}

const filteredArticles = computed(() => {
  if (navStore.hideProductsNotInStore) {
    return props.articles.filter(a => a.nopProductId !== null)
  }
  return props.articles
})

// Group by manufacturer. Pinned manufacturers from admin settings come first in
// the configured order (case-insensitive); the rest follow alphabetically.
interface MfrGroup {
  mfrName: string
  items: Article[]
}

const groupedArticles = computed<MfrGroup[]>(() => {
  const buckets = new Map<string, Article[]>()
  for (const article of filteredArticles.value) {
    const name = (article.mfrName || 'Ukjent').trim() || 'Ukjent'
    const bucket = buckets.get(name)
    if (bucket) bucket.push(article)
    else buckets.set(name, [article])
  }

  const pinnedLower = navStore.pinnedManufacturers.map(p => p.trim().toLowerCase()).filter(Boolean)
  const used = new Set<string>()
  const ordered: MfrGroup[] = []

  // 1. Pinned order, matching by lowercased name
  for (const pinLower of pinnedLower) {
    for (const [name, items] of buckets) {
      if (used.has(name)) continue
      if (name.toLowerCase() === pinLower) {
        ordered.push({ mfrName: name, items })
        used.add(name)
        break
      }
    }
  }

  // 2. Remaining alphabetically
  const remaining = Array.from(buckets.entries())
    .filter(([name]) => !used.has(name))
    .sort(([a], [b]) => a.localeCompare(b, 'nb'))
    .map(([mfrName, items]) => ({ mfrName, items }))

  return [...ordered, ...remaining]
})

const showGroupHeaders = computed(() => groupedArticles.value.length > 1)
</script>

<template>
  <div class="parts-list">
    <!-- Loading -->
    <SkeletonLoader v-if="isLoading" type="card" :lines="6" />

    <!-- Error -->
    <ErrorDisplay
      v-else-if="error"
      :message="error"
      :retryable="true"
      @retry="emit('retry')"
    />

    <!-- Empty -->
    <div v-else-if="filteredArticles.length === 0" class="parts-list__empty">
      <p>Ingen produkter i denne kategorien dessverre</p>
      <ContactForm />
    </div>

    <!-- Results -->
    <template v-else>
      <div class="parts-list__header">
        <p class="parts-list__count">{{ filteredArticles.length }} produkter funnet</p>
        <div class="parts-list__view-toggle" role="group" aria-label="Visningsmodus">
          <button
            type="button"
            class="parts-list__view-btn"
            :class="{ 'parts-list__view-btn--active': viewMode === 'grid' }"
            :aria-pressed="viewMode === 'grid'"
            aria-label="Rutenett-visning"
            title="Rutenett-visning"
            @click="setViewMode('grid')"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          <button
            type="button"
            class="parts-list__view-btn"
            :class="{ 'parts-list__view-btn--active': viewMode === 'list' }"
            :aria-pressed="viewMode === 'list'"
            aria-label="Liste-visning"
            title="Liste-visning"
            @click="setViewMode('list')"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Multi-manufacturer: section per mfr -->
      <template v-if="showGroupHeaders">
        <section
          v-for="group in groupedArticles"
          :key="group.mfrName"
          class="parts-list__section"
        >
          <h3 class="parts-list__mfr">
            {{ group.mfrName }}
            <span class="parts-list__mfr-count">({{ group.items.length }})</span>
          </h3>
          <div v-if="viewMode === 'grid'" class="parts-list__grid">
            <PartCard
              v-for="article in group.items"
              :key="article.articleNumber"
              :article="article"
            />
          </div>
          <div v-else class="parts-list__rows">
            <PartRow
              v-for="article in group.items"
              :key="article.articleNumber"
              :article="article"
            />
          </div>
        </section>
      </template>

      <!-- Single (or zero) manufacturer: flat layout -->
      <template v-else>
        <div v-if="viewMode === 'grid'" class="parts-list__grid">
          <PartCard
            v-for="article in filteredArticles"
            :key="article.articleNumber"
            :article="article"
          />
        </div>
        <div v-else class="parts-list__rows">
          <PartRow
            v-for="article in filteredArticles"
            :key="article.articleNumber"
            :article="article"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.parts-list {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  &__count {
    font-size: 0.85rem;
    color: #666;
    margin: 0;
  }

  &__view-toggle {
    display: inline-flex;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
  }

  &__view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    color: #6b7280;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;

    svg {
      flex-shrink: 0;
      display: block;
    }

    &:hover:not(&--active) {
      background: #e5e7eb;
      color: #374151;
    }

    &--active {
      background: white;
      color: #002868;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }

  &__section {
    margin-bottom: 1.5rem;

    &:last-child { margin-bottom: 0; }
  }

  &__mfr {
    font-size: 1rem;
    font-weight: 600;
    color: #002868;
    margin: 0 0 0.5rem 0;
    padding: 0.4rem 0.75rem;
    border-left: 3px solid #002868;
    background: #f5f7fb;
    border-radius: 0 4px 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__mfr-count {
    font-weight: 400;
    color: #6b7280;
    font-size: 0.85em;
    margin-left: 0.3em;
    letter-spacing: 0;
  }

  &__empty {
    text-align: center;
    padding: 3rem 1rem;
    color: #888;
    background: #f9fafb;
    border-radius: 8px;
  }
}

@media (max-width: 768px) {
  .parts-list__grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
  }
}
</style>
